import { useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ROUTES,
  buildPlaceDetailPath,
  buildSelectedPlaceMapPath,
  getSelectedPlaceMapId,
} from "@/constants/routes";
import { pushQueryParamsWithoutReload } from "@/utils/client-navigation";

export const usePlaceViewNavigation = ({ onClose = () => {}, place } = {}) => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const getCurrentQueryParams = useCallback(
    () =>
      new URLSearchParams(
        typeof window !== "undefined"
          ? window.location.search
          : searchParams.toString()
      ),
    [searchParams]
  );

  const pushQueryParams = useCallback((params) => {
    pushQueryParamsWithoutReload(params);
  }, []);

  const getPlaceReturnUrl = useCallback(() => {
    if (typeof window === "undefined") return "";

    const params = new URLSearchParams(window.location.search);
    params.delete("view-place");
    params.delete("view-place-in-list");

    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ""}${
      window.location.hash
    }`;
  }, []);

  const openPlaceView = useCallback(
    (placeId, afterOpen) => {
      if (!placeId) return;

      const currentParams = getCurrentQueryParams();
      currentParams.delete("view-place-in-list");
      currentParams.set("view-place", placeId);
      pushQueryParams(currentParams);
      afterOpen?.(placeId);
    },
    [getCurrentQueryParams, pushQueryParams]
  );

  const closePlaceView = useCallback(() => {
    onClose();
    const currentParams = getCurrentQueryParams();
    currentParams.delete("view-place");
    currentParams.delete("view-place-in-list");
    pushQueryParams(currentParams);
  }, [getCurrentQueryParams, onClose, pushQueryParams]);

  const routeToPlaceMap = useCallback(
    (placeOrId, { afterOpen, params } = {}) => {
      if (typeof window === "undefined") return;

      const placeId = getSelectedPlaceMapId(placeOrId);
      if (!placeId) return;

      if (window.location.pathname === ROUTES.current.place) {
        openPlaceView(placeId, afterOpen);
        return;
      }

      window.location.href = buildSelectedPlaceMapPath(placeOrId, params);
    },
    [openPlaceView]
  );

  const routeToPlaceDetail = useCallback(
    (eventOrPlace) => {
      if (typeof window === "undefined") return;

      const isEvent =
        eventOrPlace?.preventDefault && eventOrPlace?.stopPropagation;
      if (isEvent) {
        eventOrPlace.preventDefault();
        eventOrPlace.stopPropagation();
      }

      const nextPlace = isEvent ? place : eventOrPlace || place;
      if (!nextPlace) return;

      const placePath = buildPlaceDetailPath(nextPlace);
      if (placePath && placePath !== "/places") {
        const currentPathname = window.location.pathname;
        if (
          currentPathname === "/places" ||
          currentPathname.startsWith("/places/")
        ) {
          window.sessionStorage?.setItem(
            "hotple:place-detail:returnUrl",
            getPlaceReturnUrl()
          );
          // Remember which place was opened so the map can re-highlight its pin
          // when the user comes back (matches the marker id = placeUniqueNumber).
          const selectedId =
            nextPlace?.placeUniqueNumber ??
            nextPlace?.contentid ??
            nextPlace?.contentId ??
            nextPlace?.publicId ??
            nextPlace?._id ??
            nextPlace?.id;
          if (selectedId != null) {
            window.sessionStorage?.setItem(
              "hotple:place-detail:selectedId",
              String(selectedId)
            );
          }
        }

        // Client-side navigation (not a full page reload) — keeps the app
        // mounted so the detail opens fast, and Next's router pushes a proper
        // history entry so the browser Back button returns to the map natively.
        router.push(placePath);
      }
    },
    [getPlaceReturnUrl, place, router]
  );

  return {
    searchParams,
    openPlaceView,
    closePlaceView,
    routeToPlaceMap,
    routeToPlaceDetail,
  };
};
